/**
 * Product Gallery Masonry — Client-side interactivity
 *
 *  - True Pinterest-style masonry: CSS Grid with 1px row tracks, JS assigns
 *    each card a row span proportional to its aspect ratio.
 *  - Videos autoplay while in the viewport and pause when leaving
 *    (IntersectionObserver, threshold 0.5).
 *  - Fullscreen lightbox: open on card click, close via × / Esc / backdrop.
 *    Scroll is locked while open; the lightbox video is stopped on close.
 *  - Progressive enhancement: Liquid renders every card server-side; no
 *    external libraries, no upfront loading of large media.
 */
class ProductGalleryMasonrySection {
  constructor(gridEl) {
    this.grid = gridEl;
    this.sectionId = gridEl.dataset.sectionId;
    this.cards = Array.from(this.grid.querySelectorAll('.product-gallery-masonry__card'));
    this.lightbox = document.getElementById('ProductGalleryLightbox-' + this.sectionId);
    this.lightboxContent = this.lightbox ? this.lightbox.querySelector('[data-lightbox-content]') : null;
    this.videoObserver = null;
    this.pausedCardVideo = null;
    this.prevOverflow = '';

    this.init();
  }

  init() {
    this.recalcGridRows();
    this.initVideoObserver();
    this.bindLightbox();

    var self = this;
    window.addEventListener('resize', this.debounce(function() { self.recalcGridRows(); }, 150));
  }

  /* ================================================================
     Masonry — recompute grid row spans from each card's aspect ratio
     ================================================================ */
  recalcGridRows() {
    if (!this.grid) return;
    var style = getComputedStyle(this.grid);
    var colTpl = style.gridTemplateColumns;
    var colCount = colTpl.split(' ').length;
    if (colCount < 2) return;

    var gap     = parseFloat(style.gap) || parseFloat(style.columnGap) || 0;
    var rowGap  = parseFloat(style.rowGap) || parseFloat(style.gap) || 0;
    var trackSize = 1 + rowGap;
    var gridWidth = this.grid.clientWidth;
    if (gridWidth === 0) return;
    var colWidth = (gridWidth - gap * (colCount - 1)) / colCount;

    var calcSpan = function(cardHeight) {
      return Math.max(1, Math.ceil((cardHeight + rowGap) / trackSize));
    };

    this.cards.forEach(function(card) {
      var wrapper = card.querySelector('.product-gallery-masonry__media-wrapper');
      if (!wrapper) return;
      var inline = wrapper.getAttribute('style') || '';
      var m = inline.match(/aspect-ratio:\s*([\d.]+)\s*\/\s*([\d.]+)/);
      if (!m) return;
      var ratio = parseFloat(m[1]) / parseFloat(m[2]);
      if (!ratio) return;

      if (card.classList.contains('is-wide')) {
        // Spanning 2 columns → available width is 2 columns + 1 gap
        card.style.gridRowEnd = 'span ' + calcSpan((colWidth * 2 + gap) / ratio);
      } else {
        card.style.gridRowEnd = 'span ' + calcSpan(colWidth / ratio);
      }
    });
  }

  /* ================================================================
     Video autoplay — IntersectionObserver (threshold 0.5)
     ================================================================ */
  initVideoObserver() {
    var videos = this.cards
      .map(function(card) { return card.querySelector('video'); })
      .filter(Boolean);
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(function(v) { v.play().catch(function() {}); });
      return;
    }

    var self = this;
    this.videoObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        var video = entry.target;
        if (entry.isIntersecting) video.play().catch(function() {});
        else if (!video.paused) video.pause();
      });
    }, { threshold: 0.5 });

    videos.forEach(function(v) { self.videoObserver.observe(v); });
  }

  /* ================================================================
     Lightbox — open on card click, close via × / Esc / backdrop
     ================================================================ */
  bindLightbox() {
    if (!this.lightbox || !this.lightboxContent) return;
    var self = this;

    // Open: clicking anywhere on a card
    this.grid.addEventListener('click', function(e) {
      var card = e.target.closest('.product-gallery-masonry__card');
      if (!card) return;
      self.openLightbox(card);
    });

    // Close: × button
    var closeBtn = this.lightbox.querySelector('[data-lightbox-close]');
    if (closeBtn) closeBtn.addEventListener('click', function() { self.closeLightbox(); });

    // Close: backdrop click (media content itself does NOT close)
    this.lightbox.addEventListener('click', function(e) {
      if (e.target === self.lightbox) self.closeLightbox();
    });

    // Close: Esc
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.lightbox.hasAttribute('active')) self.closeLightbox();
    });
  }

  openLightbox(card) {
    if (!this.lightbox || !this.lightboxContent) return;
    var type = card.dataset.mediaType || 'image';
    this.lightboxContent.innerHTML = '';

    // Media is created only now → no upfront loading of large files
    if (type === 'video') {
      var video = document.createElement('video');
      video.className = 'product-gallery-masonry__lightbox-video';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = true;
      video.src = card.dataset.videoUrl || '';
      if (card.dataset.videoPoster) video.poster = card.dataset.videoPoster;
      this.lightboxContent.appendChild(video);
      video.play().catch(function() {});
    } else {
      var img = document.createElement('img');
      img.className = 'product-gallery-masonry__lightbox-image';
      img.src = card.dataset.imageUrl || '';
      img.alt = '';
      this.lightboxContent.appendChild(img);
    }

    // Pause the underlying card video while the lightbox is open
    var cardVideo = card.querySelector('video');
    if (cardVideo && !cardVideo.paused) {
      this.pausedCardVideo = cardVideo;
      cardVideo.pause();
    }

    this.lightbox.removeAttribute('hidden');
    this.lightbox.setAttribute('active', '');
    this.prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    if (!this.lightbox || !this.lightbox.hasAttribute('active')) return;
    this.lightbox.removeAttribute('active');
    this.lightbox.setAttribute('hidden', '');
    document.body.style.overflow = this.prevOverflow || '';

    // Stop / destroy lightbox video playback
    var video = this.lightboxContent.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    this.lightboxContent.innerHTML = '';

    // Resume the card video if it is still within the viewport
    if (this.pausedCardVideo) {
      var rect = this.pausedCardVideo.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        this.pausedCardVideo.play().catch(function() {});
      }
      this.pausedCardVideo = null;
    }
  }

  debounce(fn, delay) {
    var timer;
    return function() {
      var args = arguments, self = this;
      clearTimeout(timer);
      timer = setTimeout(function() { fn.apply(self, args); }, delay);
    };
  }

  /* ================================================================
     Static: Auto-init all instances
     ================================================================ */
  static initAll() {
    document.querySelectorAll('.product-gallery-masonry__grid').forEach(function(el) {
      if (!el._pgmInstance) el._pgmInstance = new ProductGalleryMasonrySection(el);
    });
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() { ProductGalleryMasonrySection.initAll(); });
} else {
  ProductGalleryMasonrySection.initAll();
}

// Shopify Theme Editor re-init
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(e) {
    var section = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!section) return;
    var el = section.querySelector('.product-gallery-masonry__grid');
    if (el && !el._pgmInstance) el._pgmInstance = new ProductGalleryMasonrySection(el);
  });
  document.addEventListener('shopify:section:unload', function(e) {
    var section = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!section) return;
    var el = section.querySelector('.product-gallery-masonry__grid');
    if (el && el._pgmInstance) {
      if (el._pgmInstance.videoObserver) el._pgmInstance.videoObserver.disconnect();
      el._pgmInstance = null;
    }
  });
}
